import _ from 'lodash'
import { ConvertToMetersObject } from './Records'

function RunValidationRulesFalsyIsValid(
  value: string | null,
  rules: [failureCondition: (v: string) => boolean, warning: string][]
): string[] {
  const errors: string[] = []

  if (!value) {
    return errors
  }

  for (const [failureCondition, warning] of rules) {
    if (failureCondition(value)) {
      errors.push(warning)
    }
  }

  return errors
}

function GenericLengthWarningMessage(
  max_length: number,
  min_length: number | null = null
): string {
  if (min_length) {
    return `should be between ${min_length} and ${max_length} characters`
  }
  return `should be no more than ${max_length} characters`
}

function startsWithPrefix(testValue: string, invalidPrefixes: string[]) {
  const capitalisedValue = testValue?.toUpperCase()
  for (const prefix of invalidPrefixes) {
    if (capitalisedValue?.startsWith(prefix.toUpperCase())) {
      return true
    }
  }
  return false
}

export const CheckAddress = (
  value: string | null,
  knownAddress: string | null
) => {
  const ADDRESS_MIN_LENGTH = 3
  const ADDRESS_MAX_LENGTH = 300
  const PERMITTED_REGEX = new RegExp(/^[a-zA-Z\d(),.*'&/\- ]*$/)

  const ADDRESS_LENGTH_ERROR_MESSAGE = GenericLengthWarningMessage(
    ADDRESS_MAX_LENGTH,
    ADDRESS_MIN_LENGTH
  )
  const ADDRESS_SPECIAL_CHARS_WARNING_MESSAGE =
    "should only contain alphanumeric characters or ( ) / - ' & , ."

  const REQUIRED_WARNING_MESSAGE = `is blank but the known address is filled in, are you sure this is correct?`

  if (knownAddress && !value) {
    return [REQUIRED_WARNING_MESSAGE]
  }
  return RunValidationRulesFalsyIsValid(value, [
    [
      v => v.length < ADDRESS_MIN_LENGTH || v.length > ADDRESS_MAX_LENGTH,
      ADDRESS_LENGTH_ERROR_MESSAGE
    ],
    [v => !PERMITTED_REGEX.test(v), ADDRESS_SPECIAL_CHARS_WARNING_MESSAGE]
  ])
}

export const CheckCarrierLicence = (value: string | null) => {
  const MAX_LENGTH = 13
  const PERMITTED_CHARS = /^[a-zA-Z0-9//]*$/g

  const LENGTH_EXCEEDED = GenericLengthWarningMessage(MAX_LENGTH)
  const INVALID_CHARS =
    'should only contain alphanumeric characters, without spaces'

  return RunValidationRulesFalsyIsValid(value, [
    [v => v.length > MAX_LENGTH, LENGTH_EXCEEDED],
    [v => !PERMITTED_CHARS.test(v), INVALID_CHARS]
  ])
}

export const CheckCarrierName = (value: string | null) => {
  const MAX_LENGTH = 65

  const LENGTH_EXCEEDED = GenericLengthWarningMessage(MAX_LENGTH)

  return RunValidationRulesFalsyIsValid(value, [
    [v => v.length > MAX_LENGTH, LENGTH_EXCEEDED]
  ])
}

export const CheckCertificationNumber = (value: string | null) => {
  const MAX_LENGTH = 20
  const PERMITTED_CHARS = /^[a-zA-Z0-9 //-]*$/g

  const LENGTH_EXCEEDED = GenericLengthWarningMessage(MAX_LENGTH)
  const INVALID_CHARS = 'should only contain alphanumeric characters or / -'

  return RunValidationRulesFalsyIsValid(value, [
    [v => v.length > MAX_LENGTH, LENGTH_EXCEEDED],
    [v => !PERMITTED_CHARS.test(v), INVALID_CHARS]
  ])
}

export const CheckCertificationResourceURI = (value: string | null) => {
  const MAX_LENGTH = 500
  const LENGTH_EXCEEDED = GenericLengthWarningMessage(MAX_LENGTH)

  return RunValidationRulesFalsyIsValid(value, [
    [v => v.length > MAX_LENGTH, LENGTH_EXCEEDED]
  ])
}

export const CheckCertificationScheme = (value: string | null) => {
  const MAX_LENGTH = 20
  const PERMITTED_CHARS = /^[a-zA-Z0-9 //-]*$/g

  const LENGTH_EXCEEDED = GenericLengthWarningMessage(MAX_LENGTH)
  const INVALID_CHARS = 'should only contain alphanumeric characters or / -'

  return RunValidationRulesFalsyIsValid(value, [
    [v => v.length > MAX_LENGTH, LENGTH_EXCEEDED],
    [v => !PERMITTED_CHARS.test(v), INVALID_CHARS]
  ])
}

export const CheckCompanyAddress = (value: string | null) => {
  const MAX_LENGTH = 300
  const LENGTH_EXCEEDED = GenericLengthWarningMessage(MAX_LENGTH)

  return RunValidationRulesFalsyIsValid(value, [
    [v => v.length > MAX_LENGTH, LENGTH_EXCEEDED]
  ])
}

export const CheckCompanyName = (value: string | null) => {
  const MAX_LENGTH = 80
  const PERMITTED_CHARS = /^[a-zA-Z0-9 ()&//-]*$/g

  const LENGTH_EXCEEDED = GenericLengthWarningMessage(MAX_LENGTH)
  const INVALID_CHARS =
    'should only contain alphanumeric characters or ( ) & / -'

  return RunValidationRulesFalsyIsValid(value, [
    [v => v.length > MAX_LENGTH, LENGTH_EXCEEDED],
    [v => !PERMITTED_CHARS.test(v), INVALID_CHARS]
  ])
}

export const CheckCompanyNumber = (value: string | null) => {
  const MAX_LENGTH = 20
  const PERMITTED_CHARS = /^[a-zA-Z0-9 //-]*$/g

  const LENGTH_EXCEEDED = GenericLengthWarningMessage(MAX_LENGTH)
  const INVALID_CHARS = 'should only contain alphanumeric characters or / -'

  return RunValidationRulesFalsyIsValid(value, [
    [v => v.length > MAX_LENGTH, LENGTH_EXCEEDED],
    [v => !PERMITTED_CHARS.test(v), INVALID_CHARS]
  ])
}

export const CheckCompanyResourceURI = (value: string | null) => {
  const MAX_LENGTH = 500
  const LENGTH_EXCEEDED = GenericLengthWarningMessage(MAX_LENGTH)

  return RunValidationRulesFalsyIsValid(value, [
    [v => v.length > MAX_LENGTH, LENGTH_EXCEEDED]
  ])
}

export const CheckConsignmentOrDeliveryNumber = (
  value: string | null,
  attachmentFileNames: string[] = []
) => {
  const MAX_LENGTH = 40
  const PERMITTED_CHARS = /^[a-zA-Z0-9 //\-:.]*$/g

  const LENGTH_EXCEEDED = GenericLengthWarningMessage(MAX_LENGTH)
  const INVALID_CHARS = 'should only contain alphanumeric characters or / - : .'
  const MATCHES_ATTACHMENT_NAME =
    'matches the name of an attached file, please check this is correct'

  // The value may be populated with one or more of the attachment file names without the file extension,
  // so we want to warn the user if the value contains any of the attachment file names (without extension) to avoid confusion.

  const attachmentFileNamesWithoutExtension = attachmentFileNames.map(
    fileName => fileName.replace(/\.[^/.]+$/, '')
  )
  const checkIfValueContainsAttachmentFileName = (v: string) => {
    for (const attachmentFileName of attachmentFileNamesWithoutExtension) {
      if (attachmentFileName && v.includes(attachmentFileName)) {
        return true
      }
    }
    return false
  }

  return RunValidationRulesFalsyIsValid(value, [
    [v => v.length > MAX_LENGTH, LENGTH_EXCEEDED],
    [v => !PERMITTED_CHARS.test(v), INVALID_CHARS],
    [
      v => checkIfValueContainsAttachmentFileName(v || ''),
      MATCHES_ATTACHMENT_NAME
    ]
  ])
}

export const CheckContainerType = (value: string | null) => {
  const MIN_LENGTH = 5
  const MAX_LENGTH = 50
  const PERMITTED_REGEX = new RegExp(/^[a-zA-Z\d,.'()/\- ]*$/)

  const LENGTH_ERROR_MESSAGE = GenericLengthWarningMessage(
    MAX_LENGTH,
    MIN_LENGTH
  )
  const SPECIAL_CHARS_WARNING_MESSAGE =
    "should only contain alphanumeric characters or / - ( ) ' , ."

  return RunValidationRulesFalsyIsValid(value, [
    [v => v.length < MIN_LENGTH || v.length > MAX_LENGTH, LENGTH_ERROR_MESSAGE],
    [v => !PERMITTED_REGEX.test(v), SPECIAL_CHARS_WARNING_MESSAGE]
  ])
}

export const CheckDateIsInThePast = (value: string | null) => {
  const DATE_WARNING = 'should be in the past'

  const errors: string[] = []
  const today = new Date().toISOString()
  if (value && value > today) {
    errors.push(DATE_WARNING)
  }

  return errors
}

export const CheckExpiryDateAfterValidFromDate = (
  expiryDate: string | null,
  validFromDate: string | null
) => {
  const DATE_WARNING = "should be after 'Valid From' date"

  const errors: string[] = []
  if (expiryDate && validFromDate && expiryDate < validFromDate) {
    errors.push(DATE_WARNING)
  }

  return errors
}

export const CheckGivenSupplierName = (value: string | null) => {
  const MAX_LENGTH = 50

  const LENGTH_EXCEEDED = GenericLengthWarningMessage(MAX_LENGTH)

  return RunValidationRulesFalsyIsValid(value, [
    [v => v.length > MAX_LENGTH, LENGTH_EXCEEDED]
  ])
}

export const CheckHeightLengthWidth = (
  value: string | null,
  maxValue = 8.0,
  unit = 'Meter',
  productGroup: string | null = null,
  dimensionType: 'height' | 'length' | 'width' | null = null
) => {
  const DECIMAL_PLACE_REGEX = new RegExp(/^-?\d*(\.\d{1,5})?$/)

  const MAX_VALUE_BY_PRODUCT_GROUP_BY_DIMENSION = {
    'Bricks & Blocks': {
      height: 3.2,
      length: maxValue,
      width: 2.5
    },
    'Concrete': {
      height: 3.5,
      length: maxValue,
      width: 3
    },
    'Concrete (pre-cast)': {
      height: 6,
      length: 20,
      width: 4
    },
    'Insulation': {
      height: maxValue,
      length: 50,
      width: maxValue
    },
    'Plasterboard': {
      height: 3.2,
      length: maxValue,
      width: maxValue
    },
    'Structural Steel': {
      height: 7,
      length: 15,
      width: 2.5
    },
    'Timber': {
      height: 3,
      length: 10,
      width: 2.5
    }
  }

  // If productGroup and dimensionType is provided, use the specific max value for that product group and dimension type.
  // Otherwise, use the general max value.
  let adjustedMaxValue = maxValue
  if (productGroup && dimensionType) {
    if (
      productGroup in MAX_VALUE_BY_PRODUCT_GROUP_BY_DIMENSION &&
      dimensionType in MAX_VALUE_BY_PRODUCT_GROUP_BY_DIMENSION[productGroup]
    ) {
      adjustedMaxValue =
        MAX_VALUE_BY_PRODUCT_GROUP_BY_DIMENSION[productGroup][dimensionType]
    }
  }

  // NOTE: Expecting Max Value to be in Meters
  const convertedValue = value
    ? parseFloat(value) * ConvertToMetersObject[unit]
    : 0
  const convertedMaxValue = _.round(
    adjustedMaxValue / ConvertToMetersObject[unit],
    1
  )

  const DECIMAL_PLACE_REGEX_WARNING_MESSAGE =
    'should have a maximum of 5 decimal places'
  const HIGH_VALUE_WARNING_MESSAGE = `should be less than ${convertedMaxValue}`

  return RunValidationRulesFalsyIsValid(value, [
    [v => !DECIMAL_PLACE_REGEX.test(v), DECIMAL_PLACE_REGEX_WARNING_MESSAGE],
    [v => convertedValue >= adjustedMaxValue, HIGH_VALUE_WARNING_MESSAGE]
  ])
}

export const CheckKnownAddress = (
  value: string | null,
  givenAddress: string | null
) => {
  const REQUIRED = `should have a location set as the RAW address is filled in`

  if (!givenAddress || value) {
    return []
  }

  return [REQUIRED]
}

export const CheckKnownContainerType = (
  value: string | null,
  containerType: string | null
) => {
  const NO_MATCH = `could not make a match`
  const warnings: string[] = []

  // Check if knownContainerType is blank but containerType has a value
  // Trim containerType to treat whitespace-only strings as empty
  if (!value && containerType?.trim()) {
    warnings.push(NO_MATCH)
  }

  return warnings
}

export const CheckNotes = (value: string | null) => {
  const MAX_LENGTH = 100
  const PERMITTED_CHARS = /^[a-zA-Z0-9 //\-,.&]*$/g

  const LENGTH_EXCEEDED = GenericLengthWarningMessage(MAX_LENGTH)
  const INVALID_CHARS = `should only contain alphanumeric characters or / , - . &`

  return RunValidationRulesFalsyIsValid(value, [
    [v => v.length > MAX_LENGTH, LENGTH_EXCEEDED],
    [v => !PERMITTED_CHARS.test(v), INVALID_CHARS]
  ])
}

export const CheckProductName = (value: string | null) => {
  const MAX_LENGTH = 150

  const LENGTH_EXCEEDED = GenericLengthWarningMessage(MAX_LENGTH)

  return RunValidationRulesFalsyIsValid(value, [
    [v => v.length > MAX_LENGTH, LENGTH_EXCEEDED]
  ])
}

export const CheckProductType = (value: string | null) => {
  const MAX_LENGTH = 50
  const PERMITTED_CHARS = /^[a-zA-Z0-9 //-]*$/g

  const LENGTH_EXCEEDED = GenericLengthWarningMessage(MAX_LENGTH)
  const INVALID_CHARS = 'should only contain alphanumeric characters or / -'

  return RunValidationRulesFalsyIsValid(value, [
    [v => v.length > MAX_LENGTH, LENGTH_EXCEEDED],
    [v => !PERMITTED_CHARS.test(v), INVALID_CHARS]
  ])
}

export const CheckQuantity = (value: string | null) => {
  const MAX_LENGTH = 5
  const MAX_VALUE = 200
  const LENGTH_EXCEEDED = GenericLengthWarningMessage(MAX_LENGTH)
  const HIGH_VALUE_WARNING_MESSAGE = `should be ${MAX_VALUE} or less`

  return RunValidationRulesFalsyIsValid(value, [
    [v => v.length > MAX_LENGTH, LENGTH_EXCEEDED],
    [v => parseInt(v) >= MAX_VALUE, HIGH_VALUE_WARNING_MESSAGE]
  ])
}

export const CheckReceiverOrSupplierOrSenderOrCarrierReference = (
  value: string | null
) => {
  const MAX_LENGTH = 50
  const PERMITTED_CHARS = /^[a-zA-Z0-9 //\-,.]*$/g

  const LENGTH_EXCEEDED = GenericLengthWarningMessage(MAX_LENGTH)
  const INVALID_CHARS = `should only contain alphanumeric characters or / , - .`

  return RunValidationRulesFalsyIsValid(value, [
    [v => v.length > MAX_LENGTH, LENGTH_EXCEEDED],
    [v => !PERMITTED_CHARS.test(v), INVALID_CHARS]
  ])
}

export const CheckSubContractor = (
  value: string | null,
  projectName: string | null
) => {
  const MAX_LENGTH = 50

  const LENGTH_EXCEEDED = GenericLengthWarningMessage(MAX_LENGTH)
  const CONTAINS_PROJECT_NAME =
    'must not match project name; if no alternative leave blank.'

  return RunValidationRulesFalsyIsValid(value, [
    [v => v.length > MAX_LENGTH, LENGTH_EXCEEDED],
    [
      v => v?.toUpperCase() === projectName?.toUpperCase(),
      CONTAINS_PROJECT_NAME
    ]
  ])
}

export const CheckTradeContractor = (
  value: string | null,
  projectName: string | null
) => {
  const INVALID_PREFIXES = ['c/o']
  const MAX_LENGTH = 40

  const LENGTH_EXCEEDED = GenericLengthWarningMessage(MAX_LENGTH)
  const CONTAINS_PREFIX =
    'should not contain c/o - there should only be one contractor'
  const CONTAINS_PROJECT_NAME =
    'must not match project name; if no alternative leave blank.'

  return RunValidationRulesFalsyIsValid(value, [
    [v => v.length > MAX_LENGTH, LENGTH_EXCEEDED],
    [v => startsWithPrefix(v, INVALID_PREFIXES), CONTAINS_PREFIX],
    [
      v => v?.toUpperCase() === projectName?.toUpperCase(),
      CONTAINS_PROJECT_NAME
    ]
  ])
}

export const CheckVehicleReg = (value: string | null) => {
  const VALID_REG =
    /(?<Current>^[A-Z]{2}[0-9]{2}[A-Z]{3}$)|(?<Prefix>^[A-Z][0-9]{1,3}[A-Z]{3}$)|(?<Suffix>^[A-Z]{3}[0-9]{1,3}[A-Z]$)|(?<DatelessLongNumberPrefix>^[0-9]{1,4}[A-Z]{1,2}$)|(?<DatelessShortNumberPrefix>^[0-9]{1,3}[A-Z]{1,3}$)|(?<DatelessLongNumberSuffix>^[A-Z]{1,2}[0-9]{1,4}$)|(?<DatelessShortNumberSufix>^[A-Z]{1,3}[0-9]{1,3}$)|(?<DatelessNorthernIreland>^[A-Z]{1,3}[0-9]{1,4}$)|(?<DiplomaticPlate>^[0-9]{3}[DX]{1}[0-9]{3}$)/g

  const INVALID_REG = 'does not appear to be a valid vehicle registration'

  return RunValidationRulesFalsyIsValid(value, [
    [v => !VALID_REG.test(v.replace(' ', '')), INVALID_REG]
  ])
}

export const CheckVolume = (
  value: string | null,
  unit: string | null,
  productGroup: string | null = null
) => {
  const DECIMAL_PLACE_REGEX = new RegExp(/^-?\d*(\.\d{1,5})?$/)
  const MAX_VALUE_BY_UNIT = {
    CubicMeter: 85.5,
    CubicYard: 27,
    ImperialGallon: 18807,
    Liter: 85500,
    UsGallon: 5453
  }
  const MAX_VALUE_BY_PRODUCT_GROUP_BY_UNIT = {
    'Aggregate': {
      CubicMeter: 27,
      ImperialGallon: 5939,
      Liter: 27000
    },
    'Bricks & Blocks': {
      CubicMeter: 17,
      ImperialGallon: 3739,
      Liter: 17000
    },
    'Concrete': {
      CubicMeter: 8.1,
      ImperialGallon: 1781,
      Liter: 8100
    },
    'Fuels': {
      CubicMeter: 34,
      ImperialGallon: 7479,
      Liter: 34000
    },
    'Insulation': {
      CubicMeter: 86,
      ImperialGallon: 18917,
      Liter: 86000
    },
    'Plasterboard': {
      CubicMeter: 24,
      ImperialGallon: 5279,
      Liter: 24000
    },
    'Rebar': {
      CubicMeter: 0,
      ImperialGallon: 0,
      Liter: 0
    },
    'Structural Steel': {
      CubicMeter: 28,
      ImperialGallon: 6159,
      Liter: 28000
    },
    'Timber': {
      CubicMeter: 53,
      ImperialGallon: 11658,
      Liter: 53000
    }
  }

  // If productGroup and unit is provided, use the specific max value for that product group and unit.
  // Otherwise, use the general max value for the unit.
  let MAX_VALUE = 1000
  if (productGroup && unit) {
    if (
      productGroup in MAX_VALUE_BY_PRODUCT_GROUP_BY_UNIT &&
      unit in MAX_VALUE_BY_PRODUCT_GROUP_BY_UNIT[productGroup]
    ) {
      MAX_VALUE = MAX_VALUE_BY_PRODUCT_GROUP_BY_UNIT[productGroup][unit]
    } else if (unit in MAX_VALUE_BY_UNIT) {
      MAX_VALUE = MAX_VALUE_BY_UNIT[unit]
    }
  } else if (unit && unit in MAX_VALUE_BY_UNIT) {
    MAX_VALUE = MAX_VALUE_BY_UNIT[unit]
  }

  const DECIMAL_PLACE_REGEX_WARNING_MESSAGE =
    'should have a maximum of 5 decimal places'
  const HIGH_VALUE_WARNING_MESSAGE = `should be ${MAX_VALUE} or less`

  return RunValidationRulesFalsyIsValid(value, [
    [v => !DECIMAL_PLACE_REGEX.test(v), DECIMAL_PLACE_REGEX_WARNING_MESSAGE],
    [v => parseFloat(v) > MAX_VALUE, HIGH_VALUE_WARNING_MESSAGE]
  ])
}

export const CheckWastePermit = (value: string | null) => {
  const MIN_LENGTH = 8
  const MAX_LENGTH = 14
  const MAX_HYPHENATED_LENGTH = 20
  const ALLOWED_CHARACTERS = new RegExp(/^[a-zA-Z\d/-]*$/)
  const MORE_THAN_TWO_SLASHES = /(?:.*\/){3,}/

  const INCORRECT_LENGTH = `should be between ${MIN_LENGTH} and ${MAX_LENGTH} characters, or up to ${MAX_HYPHENATED_LENGTH} if it contains a hyphen '-'`
  const CONTAINS_SYMBOLS = 'should only contain alphanumeric characters or /-'
  const TOO_MANY_SLASHES = 'should not contain more than 2 /'
  const UNSLASHED_WML = 'should not contain WML without an accompanying /'
  const NON_SEPA_OR_NRW_WME_HYPHEN = `, unexpected hyphen present`

  return RunValidationRulesFalsyIsValid(value, [
    [
      v =>
        v.length < MIN_LENGTH ||
        (/-/i.test(v)
          ? v.length > MAX_HYPHENATED_LENGTH
          : v.length > MAX_LENGTH),
      INCORRECT_LENGTH
    ],
    [v => !ALLOWED_CHARACTERS.test(v), CONTAINS_SYMBOLS],
    [v => MORE_THAN_TWO_SLASHES.test(v), TOO_MANY_SLASHES],
    [v => /WML/i.test(v) && !/WML\//i.test(v), UNSLASHED_WML],
    [
      v =>
        /-/.test(v) &&
        !/SEPA/i.test(v) &&
        !v.toUpperCase().startsWith('NRW-WME'),
      NON_SEPA_OR_NRW_WME_HYPHEN
    ]
  ])
}

export const CheckWasteSICCode = (value: string | null) => {
  // MAX LENGTH is 5 not including any . or /
  const MAX_LENGTH = 5
  const PERMITTED_CHARS = /^[0-9./]*$/g

  const LENGTH_EXCEEDED = `should contain no more than ${MAX_LENGTH} numbers`
  const INVALID_CHARS = 'should only contain numbers or . /'

  return RunValidationRulesFalsyIsValid(value, [
    [
      v => v.replace(/\./g, '').replace(/\//g, '').length > MAX_LENGTH,
      LENGTH_EXCEEDED
    ],
    [v => !PERMITTED_CHARS.test(v), INVALID_CHARS]
  ])
}

export const CheckWeight = (
  value: string | null,
  unit: string | null,
  productGroup: string | null = null
) => {
  const DECIMAL_PLACE_REGEX = new RegExp(/^-?\d*(\.\d{1,5})?$/)
  const MAX_VALUE_BY_UNIT = {
    Kilogram: 1000000,
    Pound: 471978,
    ShortTon: 110,
    Tonne: 100
  }
  const MAX_VALUE_BY_PRODUCT_GROUP_BY_UNIT = {
    'Aggregate': {
      Kilogram: 39000,
      Tonne: 39
    },
    'Bricks & Blocks': {
      Kilogram: 26000,
      Tonne: 26
    },
    'Fuels': {
      Kilogram: 110,
      Tonne: 0.11
    },
    'Insulation': {
      Kilogram: 28600,
      Tonne: 28.6
    },
    'Plasterboard': {
      Kilogram: 21000,
      Tonne: 21
    },
    'Rebar': {
      Kilogram: 31000,
      Tonne: 31
    },
    'Structural Steel': {
      Kilogram: 32000,
      Tonne: 32
    },
    'Timber': {
      Kilogram: 574000,
      Tonne: 574
    }
  }

  // If productGroup and unit is provided, use the specific max value for that product group and unit.
  // Otherwise, use the general max value for the unit.
  let MAX_VALUE = 1000
  if (productGroup && unit) {
    if (
      productGroup in MAX_VALUE_BY_PRODUCT_GROUP_BY_UNIT &&
      unit in MAX_VALUE_BY_PRODUCT_GROUP_BY_UNIT[productGroup]
    ) {
      MAX_VALUE = MAX_VALUE_BY_PRODUCT_GROUP_BY_UNIT[productGroup][unit]
    } else if (unit in MAX_VALUE_BY_UNIT) {
      MAX_VALUE = MAX_VALUE_BY_UNIT[unit]
    }
  } else if (unit && unit in MAX_VALUE_BY_UNIT) {
    MAX_VALUE = MAX_VALUE_BY_UNIT[unit]
  }

  const DECIMAL_PLACE_REGEX_WARNING_MESSAGE =
    'should have a maximum of 5 decimal places'
  const HIGH_VALUE_WARNING_MESSAGE = `should be ${MAX_VALUE} or less`

  return RunValidationRulesFalsyIsValid(value, [
    [v => !DECIMAL_PLACE_REGEX.test(v), DECIMAL_PLACE_REGEX_WARNING_MESSAGE],
    [v => parseFloat(v) > MAX_VALUE, HIGH_VALUE_WARNING_MESSAGE]
  ])
}
